/**
 * FunnelEdgeLabel - Label customizado para edges do funil
 * 
 * Exibe quantidade de transições e percentual de conversão.
 * Para nodes multi-output (com sourceHandle), o badge fica próximo ao
 * ponto de saída do node. Para saída única, fica no centro da edge.
 * Dots animados percorrem a edge visualizando o fluxo de leads.
 * 
 * @release v1.1.0 - 2026-03-07 - Badge reposicionado próximo ao source handle
 * @release v1.0.0 - 2026-03-07 - Versão inicial com animação de dots
 */

import { memo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react';
import { cn } from '@/lib/utils';
import type { FunnelEdgeStats } from '@/hooks/useFlowFunnelStats';

/** Props do componente FunnelEdge */
/** Dados de estatísticas do funil OU de variante A/B */
interface FunnelEdgeData {
  stats?: FunnelEdgeStats;
  /** Campos específicos de A/B Test (mesmo formato do ABTestEdge) */
  variant?: string;
  count?: number;
  percentage?: number;
  total?: number;
  [key: string]: unknown;
}

/**
 * Edge customizada do funil com métricas inline e dots animados
 */
export const FunnelEdge = memo(function FunnelEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  sourceHandleId,
  data,
  style,
}: EdgeProps) {
  const edgeData = data as FunnelEdgeData | undefined;
  const stats = edgeData?.stats;

  // Detecta se é uma edge de A/B Test com dados de variante
  const isABTest = !!edgeData?.variant && edgeData?.total !== undefined;
  const abVariant = edgeData?.variant;
  const abCount = edgeData?.count ?? 0;
  const abPercentage = edgeData?.percentage ?? 0;
  const abTotal = edgeData?.total ?? 0;
  const hasABData = isABTest && abTotal > 0;

  // Calcula o path bezier da edge
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const transitions = stats?.transitions ?? 0;
  const percentage = stats?.percentage ?? 0;
  const hasData = transitions > 0 || hasABData;

  // Determina se é um node multi-output (possui sourceHandle definido)
  const isMultiOutput = !!sourceHandleId;

  // Posição do badge: sempre no ponto médio da curva bezier para evitar sobreposição
  const badgeX = labelX;
  const badgeY = labelY;

  // Cor neutra para todas as linhas — sem variação semântica por percentual
  const edgeColor = 'hsl(var(--muted-foreground))';

  // Classes de fundo pastel para o badge — tons suaves e legíveis
  const badgeBgClass = isMultiOutput
    ? percentage >= 70
      ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
      : percentage >= 30
        ? 'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
        : 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
    : 'bg-card border-border';

  // Quantidade de dots proporcional ao volume (1 a 3)
  const dotCount = hasData ? Math.min(Math.ceil(transitions / 5), 3) : 0;

  // Velocidade inversamente proporcional ao volume (mais leads = mais rápido)
  const duration = Math.max(1.5, 4 - transitions * 0.05);

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          stroke: isABTest ? '#ec4899' : edgeColor,
          strokeWidth: hasData ? Math.max(2, Math.min((isABTest ? abCount : transitions) / 10, 6)) : 1.5,
          opacity: hasData ? 0.8 : 0.6,
        }}
      />

      {/* Dots animados percorrendo a edge — visualização do fluxo de leads */}
      {hasData && Array.from({ length: dotCount }).map((_, i) => (
        <circle
          key={`dot-${id}-${i}`}
          r="3.5"
          fill={edgeColor}
          opacity={0.9}
        >
          <animateMotion
            dur={`${duration}s`}
            repeatCount="indefinite"
            begin={`${i * (duration / dotCount)}s`}
            path={edgePath}
          />
        </circle>
      ))}

      {/* Badge A/B Test — estilo rosa com variante, % e contagem */}
      {hasABData && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${badgeX}px, ${badgeY}px)`,
              pointerEvents: 'all',
              zIndex: 1000,
            }}
            className="nodrag nopan"
          >
            <div className="bg-[#ec4899]/20 border border-[#ec4899] rounded-md px-2 py-1 text-xs font-medium shadow-sm backdrop-blur-sm">
              <div className="flex flex-col items-center gap-0.5">
                {/* Variante e porcentagem */}
                <span className="text-[#ec4899] font-bold">
                  {abVariant}: {abPercentage}%
                </span>
                {/* Contagem absoluta */}
                <span className="text-muted-foreground text-[10px]">
                  ({abCount} de {abTotal})
                </span>
              </div>
            </div>
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Badge de métricas padrão — layout em coluna com transições e % de conversão */}
      {!hasABData && hasData && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${badgeX}px, ${badgeY}px)`,
              pointerEvents: 'all',
              zIndex: 1000,
            }}
            className="nodrag nopan"
          >
            <div className={cn(
              'rounded-md px-2 py-1 text-xs font-medium shadow-sm backdrop-blur-sm',
              isMultiOutput
                ? badgeBgClass
                : 'bg-card/90 border border-border',
            )}>
              <div className="flex flex-col items-center gap-0.5">
                {/* Quantidade de transições */}
                <span className={cn(
                  'font-bold',
                  isMultiOutput ? '' : 'text-foreground',
                )}>
                  {transitions} {transitions === 1 ? 'lead' : 'leads'}
                </span>
                {/* Porcentagem de conversão com cor semântica */}
                <span className={cn(
                  'text-[10px]',
                  isMultiOutput
                    ? 'opacity-70'
                    : cn(
                        percentage >= 70 && 'text-green-600 dark:text-green-400',
                        percentage >= 30 && percentage < 70 && 'text-yellow-600 dark:text-yellow-400',
                        percentage < 30 && 'text-red-600 dark:text-red-400',
                      )
                )}>
                  {percentage.toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
});